import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import {
    Shield, CheckCircle2, Clock, AlertTriangle, XCircle,
    Search, Music, Play, Pause, ChevronDown, Loader2, FileText
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
    approved: { label: 'Approved', icon: CheckCircle2, color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
    pending: { label: 'Pending', icon: Clock, color: 'text-yellow-400', bg: 'bg-yellow-500/10' },
    flagged: { label: 'Flagged', icon: AlertTriangle, color: 'text-orange-400', bg: 'bg-orange-500/10' },
    rejected: { label: 'Rejected', icon: XCircle, color: 'text-red-400', bg: 'bg-red-500/10' },
};

export default function AdminModeration() {
    const [tracks, setTracks] = useState([]);
    const [stats, setStats] = useState({ total: 0, approved: 0, pending: 0, flagged: 0, rejected: 0 });
    const [loading, setLoading] = useState(true);
    const [tab, setTab] = useState('all');
    const [search, setSearch] = useState('');
    const [selected, setSelected] = useState(new Set());
    const [updating, setUpdating] = useState(null);
    const [playingId, setPlayingId] = useState(null);
    const audioRef = useState(() => new Audio())[0];

    const fetchData = useCallback(async () => {
        try {
            const params = new URLSearchParams();
            if (tab !== 'all') params.set('status', tab);
            if (search) params.set('search', search);
            const res = await axios.get(`${API}/api/admin/moderation?${params}`, { withCredentials: true });
            setTracks(res.data.tracks);
            setStats(res.data.stats);
        } catch (e) {
            toast.error('Failed to load moderation data');
        } finally {
            setLoading(false);
        }
    }, [tab, search]);

    useEffect(() => {
        setLoading(true);
        fetchData();
    }, [fetchData]);

    const updateStatus = async (audioId, newStatus, note = '') => {
        setUpdating(audioId);
        try {
            await axios.put(`${API}/api/admin/moderation/${audioId}`, { status: newStatus, note }, { withCredentials: true });
            toast.success(`Track ${newStatus}`);
            fetchData();
        } catch (e) {
            toast.error('Failed to update status');
        } finally {
            setUpdating(null);
        }
    };

    const bulkAction = async (newStatus) => {
        if (selected.size === 0) return;
        try {
            await axios.put(`${API}/api/admin/moderation/bulk-action`, {
                audio_ids: Array.from(selected),
                status: newStatus
            }, { withCredentials: true });
            toast.success(`${selected.size} tracks ${newStatus}`);
            setSelected(new Set());
            fetchData();
        } catch (e) {
            toast.error('Bulk action failed');
        }
    };

    const toggleSelect = (id) => {
        setSelected(prev => {
            const n = new Set(prev);
            n.has(id) ? n.delete(id) : n.add(id);
            return n;
        });
    };

    const selectAll = () => {
        if (selected.size === tracks.length) {
            setSelected(new Set());
        } else {
            setSelected(new Set(tracks.map(t => t.id)));
        }
    };

    const togglePlay = (track) => {
        if (playingId === track.id) {
            audioRef.pause();
            setPlayingId(null);
        } else if (track.file_path) {
            audioRef.src = `${API}/api/files/${track.file_path}`;
            audioRef.play();
            setPlayingId(track.id);
        }
    };

    if (loading && tracks.length === 0) {
        return (
            <DashboardLayout>
                <div className="flex items-center justify-center h-64">
                    <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" />
                </div>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout>
            <div className="space-y-6" data-testid="admin-moderation">
                {/* Header */}
                <div>
                    <h1 className="font-outfit text-2xl lg:text-3xl font-bold text-white flex items-center gap-3">
                        <Shield className="w-7 h-7 text-gold" />
                        Content Moderation
                    </h1>
                    <p className="text-muted-foreground mt-1">Review and manage audio content across the platform</p>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
                    {[
                        { key: 'total', label: 'Total', value: stats.total, icon: Music, color: 'text-white', bg: 'bg-white/5' },
                        { key: 'approved', label: 'Approved', value: stats.approved, icon: CheckCircle2, color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
                        { key: 'pending', label: 'Pending', value: stats.pending, icon: Clock, color: 'text-yellow-400', bg: 'bg-yellow-500/10' },
                        { key: 'flagged', label: 'Flagged', value: stats.flagged, icon: AlertTriangle, color: 'text-orange-400', bg: 'bg-orange-500/10' },
                        { key: 'rejected', label: 'Rejected', value: stats.rejected, icon: XCircle, color: 'text-red-400', bg: 'bg-red-500/10' },
                    ].map(s => (
                        <div key={s.key} className={`p-4 rounded-xl border border-white/10 ${s.bg}`} data-testid={`stat-${s.key}`}>
                            <div className="flex items-center gap-2 mb-2">
                                <s.icon className={`w-4 h-4 ${s.color}`} />
                                <span className="text-xs text-muted-foreground uppercase tracking-wider">{s.label}</span>
                            </div>
                            <p className={`text-2xl font-outfit font-bold ${s.color}`}>{s.value}</p>
                        </div>
                    ))}
                </div>

                {/* Filters + Search */}
                <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
                    <Tabs value={tab} onValueChange={(v) => { setTab(v); setSelected(new Set()); }}>
                        <TabsList className="bg-[#121620] border border-white/10">
                            <TabsTrigger value="all" className="data-[state=active]:bg-gold data-[state=active]:text-[#0A0D14]">All</TabsTrigger>
                            <TabsTrigger value="approved" className="data-[state=active]:bg-gold data-[state=active]:text-[#0A0D14]">Approved</TabsTrigger>
                            <TabsTrigger value="pending" className="data-[state=active]:bg-gold data-[state=active]:text-[#0A0D14]">Pending</TabsTrigger>
                            <TabsTrigger value="flagged" className="data-[state=active]:bg-gold data-[state=active]:text-[#0A0D14]">Flagged</TabsTrigger>
                            <TabsTrigger value="rejected" className="data-[state=active]:bg-gold data-[state=active]:text-[#0A0D14]">Rejected</TabsTrigger>
                        </TabsList>
                    </Tabs>

                    <div className="relative w-full sm:w-64">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                            placeholder="Search tracks..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="pl-9 bg-[#121620] border-white/10"
                            data-testid="search-input"
                        />
                    </div>
                </div>

                {/* Bulk Actions */}
                {selected.size > 0 && (
                    <div className="flex items-center gap-3 p-3 rounded-xl bg-gold/10 border border-gold/20 animate-fade-in">
                        <span className="text-sm text-gold font-medium">{selected.size} selected</span>
                        <div className="flex gap-2 ml-auto">
                            <Button size="sm" className="bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30" onClick={() => bulkAction('approved')} data-testid="bulk-approve">
                                <CheckCircle2 className="w-4 h-4 mr-1" /> Approve
                            </Button>
                            <Button size="sm" className="bg-orange-500/20 text-orange-400 hover:bg-orange-500/30" onClick={() => bulkAction('flagged')} data-testid="bulk-flag">
                                <AlertTriangle className="w-4 h-4 mr-1" /> Flag
                            </Button>
                            <Button size="sm" className="bg-red-500/20 text-red-400 hover:bg-red-500/30" onClick={() => bulkAction('rejected')} data-testid="bulk-reject">
                                <XCircle className="w-4 h-4 mr-1" /> Reject
                            </Button>
                        </div>
                    </div>
                )}

                {/* Track List */}
                <div className="space-y-2">
                    {tracks.length > 0 && (
                        <div className="flex items-center gap-3 px-4 py-2 text-xs text-muted-foreground uppercase tracking-wider">
                            <input
                                type="checkbox"
                                checked={selected.size === tracks.length && tracks.length > 0}
                                onChange={selectAll}
                                className="rounded border-white/20 accent-gold"
                            />
                            <span className="flex-1">Track</span>
                            <span className="w-32 hidden sm:block">Attraction</span>
                            <span className="w-20 hidden sm:block">Language</span>
                            <span className="w-24">Status</span>
                            <span className="w-40">Actions</span>
                        </div>
                    )}

                    {tracks.map((track) => {
                        const statusCfg = STATUS_CONFIG[track.moderation_status] || STATUS_CONFIG.approved;
                        const StatusIcon = statusCfg.icon;
                        const isSelected = selected.has(track.id);
                        const isPlaying = playingId === track.id;
                        const imgUrl = track.image_url
                            ? (track.image_url.startsWith('http') ? track.image_url : `${API}${track.image_url}`)
                            : null;

                        return (
                            <div
                                key={track.id}
                                className={`flex items-center gap-3 p-4 rounded-xl border transition-all ${
                                    isSelected
                                        ? 'bg-gold/5 border-gold/20'
                                        : 'bg-[#121620] border-white/10 hover:border-white/20'
                                }`}
                                data-testid={`mod-track-${track.id}`}
                            >
                                <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() => toggleSelect(track.id)}
                                    className="rounded border-white/20 accent-gold shrink-0"
                                />

                                {/* Play + Image */}
                                <button onClick={() => togglePlay(track)} className="relative w-10 h-10 rounded-lg overflow-hidden shrink-0">
                                    {imgUrl ? (
                                        <img src={imgUrl} alt="" className="w-full h-full object-cover" />
                                    ) : (
                                        <div className="w-full h-full bg-white/5 flex items-center justify-center">
                                            <Music className="w-4 h-4 text-muted-foreground" />
                                        </div>
                                    )}
                                    <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                                        {isPlaying ? <Pause className="w-3 h-3 text-white" /> : <Play className="w-3 h-3 text-white ml-0.5" />}
                                    </div>
                                </button>

                                {/* Title */}
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm text-white font-medium truncate">{track.title}</p>
                                    <p className="text-xs text-muted-foreground truncate sm:hidden">{track.attraction_title}</p>
                                    {track.transcript && (
                                        <span className="inline-flex items-center gap-1 text-xs text-gold/60 mt-0.5">
                                            <FileText className="w-3 h-3" /> Transcript
                                        </span>
                                    )}
                                </div>

                                {/* Attraction */}
                                <span className="w-32 text-sm text-muted-foreground truncate hidden sm:block">{track.attraction_title}</span>

                                {/* Language */}
                                <span className="w-20 text-xs text-muted-foreground uppercase hidden sm:block">{track.language}</span>

                                {/* Status Badge */}
                                <div className={`w-24 flex items-center gap-1.5 px-2 py-1 rounded-full text-xs ${statusCfg.bg} ${statusCfg.color}`}>
                                    <StatusIcon className="w-3 h-3" />
                                    {statusCfg.label}
                                </div>

                                {/* Actions */}
                                <div className="w-40 flex items-center gap-1 shrink-0">
                                    {track.moderation_status !== 'approved' && (
                                        <Button
                                            size="sm"
                                            variant="ghost"
                                            disabled={updating === track.id}
                                            onClick={() => updateStatus(track.id, 'approved')}
                                            className="text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10 px-2 h-8"
                                            data-testid={`approve-${track.id}`}
                                        >
                                            {updating === track.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                                        </Button>
                                    )}
                                    {track.moderation_status !== 'flagged' && (
                                        <Button
                                            size="sm"
                                            variant="ghost"
                                            disabled={updating === track.id}
                                            onClick={() => updateStatus(track.id, 'flagged')}
                                            className="text-orange-400 hover:text-orange-300 hover:bg-orange-500/10 px-2 h-8"
                                            data-testid={`flag-${track.id}`}
                                        >
                                            <AlertTriangle className="w-4 h-4" />
                                        </Button>
                                    )}
                                    {track.moderation_status !== 'rejected' && (
                                        <Button
                                            size="sm"
                                            variant="ghost"
                                            disabled={updating === track.id}
                                            onClick={() => updateStatus(track.id, 'rejected')}
                                            className="text-red-400 hover:text-red-300 hover:bg-red-500/10 px-2 h-8"
                                            data-testid={`reject-${track.id}`}
                                        >
                                            <XCircle className="w-4 h-4" />
                                        </Button>
                                    )}
                                </div>
                            </div>
                        );
                    })}

                    {tracks.length === 0 && !loading && (
                        <div className="text-center py-16 bg-[#121620] rounded-xl border border-white/10">
                            <Shield className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                            <h3 className="text-white font-outfit font-semibold text-lg mb-2">No content found</h3>
                            <p className="text-muted-foreground text-sm">
                                {tab === 'all' ? 'No audio content to moderate' : `No ${tab} content`}
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </DashboardLayout>
    );
}
